import React from 'react';
import { useActor } from '@xstate/react';
import CaseForm, { FormValues } from '../case/CaseForm';
import SubmitCaseViewProps from './types/SubmitCaseViewProps';
import { useSubmitCaseContext } from '../../context/SubmitCaseContext';
import { IonButton } from '@ionic/react';
import SnackbarContext from '../../context/SnackbarContext';
import styled from 'styled-components';

const ValidationError = styled.p`
  margin: 0 0 8px 0;
  font-size: 13px;
  color: var(--mdc-theme-error);
  text-align: center;
`;

interface EnterInfoProps extends SubmitCaseViewProps {
  onSubmit?: (values: FormValues) => void;
}

const FIELD_SCROLL_ORDER = [
  'submitterFirstName',
  'submitterLastName',
  'submitterEmailAddresses',
  'patientFirstName',
  'patientLastName',
  'age',
  'species',
  'sex',
  'fixed',
  'sites',
];

const EnterInfo: React.FC<EnterInfoProps> = ({ currentUser }) => {
  const { submitCaseService } = useSubmitCaseContext();
  const [state, send] = useActor(submitCaseService);
  const { setMessage } = React.useContext(SnackbarContext);
  const [validationError, setValidationError] = React.useState<string | null>(null);

  return (
    <CaseForm
      case={state.context.caseInfo}
      initialValues={state.context.caseInfo}
      title="Submit case"
      onSubmit={async (values) => {
        send({ type: 'NEXT', caseInfo: values });
      }}
      onChange={() => setValidationError(null)}
      renderButtons={({ isSubmitting, submitForm, validateForm, setTouched, values }) => (
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          {validationError && (
            <ValidationError>{validationError}</ValidationError>
          )}
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <IonButton
              fill="clear"
              type="button"
              onClick={() => send({ type: 'BACK' })}
            >
              Back
            </IonButton>
            <IonButton
              type="button"
              disabled={isSubmitting}
              onClick={async () => {
                setTouched(
                  {
                    submitterFirstName: true,
                    submitterLastName: true,
                    submitterEmailAddresses: values.submitterEmailAddresses.map(() => true),
                    patientFirstName: true,
                    patientLastName: true,
                    internalReferenceNumber: true,
                    age: true,
                    ageMonths: true,
                    species: true,
                    sex: true,
                    fixed: true,
                    description: true,
                    sites: true,
                  },
                  false
                );
                const errors = await validateForm();
                if (Object.keys(errors).length === 0) {
                  setValidationError(null);
                  submitForm();
                  return;
                }
                setValidationError('Please fill in all required fields before proceeding.');
                const firstError = FIELD_SCROLL_ORDER.find((f) => f in errors);
                if (firstError === 'sites') {
                  setMessage({ text: 'Please add at least one site before proceeding.' });
                } else if (firstError) {
                  const selector =
                    firstError === 'submitterEmailAddresses'
                      ? '[name="submitterEmailAddresses[0]"]'
                      : `#${firstError}`;
                  document
                    .querySelector(selector)
                    ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }
              }}
            >
              Payment
            </IonButton>
          </div>
        </div>
      )}
    />
  );
};

export default EnterInfo;
